import emojiDataSource from 'emoji-datasource/emoji.json';
import materialSymbols from 'material-icons/_data/versions.json';

import {
  EmojiDataSourceItem,
  EmojiItem,
  EmojiSkinVariation,
  SkinColour,
  SymbolGroup,
  SymbolItem,
} from './types';

export enum EmojiCategory {
  RecentlyUsed = 'app.emoji.category.recently_used',
  SmileysEmotion = 'app.emoji.category.smileys_emotion',
  PeopleBody = 'app.emoji.category.people_body',
  AnimalsNature = 'app.emoji.category.animals_nature',
  FoodDrink = 'app.emoji.category.food_drink',
  TravelPlaces = 'app.emoji.category.travel_places',
  Activities = 'app.emoji.category.activities',
  Objects = 'app.emoji.category.objects',
  Symbols = 'app.emoji.category.symbols',
  Flags = 'app.emoji.category.flags',
  SkinTones = 'app.emoji.category.skin_tones',
}

const categoryMap: Record<string, EmojiCategory> = {
  'Smileys & Emotion': EmojiCategory.SmileysEmotion,
  'People & Body': EmojiCategory.PeopleBody,
  'Animals & Nature': EmojiCategory.AnimalsNature,
  'Food & Drink': EmojiCategory.FoodDrink,
  'Travel & Places': EmojiCategory.TravelPlaces,
  Activities: EmojiCategory.Activities,
  Objects: EmojiCategory.Objects,
  Symbols: EmojiCategory.Symbols,
  Flags: EmojiCategory.Flags,
  Component: EmojiCategory.SkinTones,
};

export const unifiedToEmoji = (unicode: string): string =>
  unicode
    .split('-')
    .map(code => String.fromCodePoint(<any>`0x${code}`))
    .join('');

export const EMOJIS = (<EmojiDataSourceItem[]>emojiDataSource).reduce((acc, item) => {
  const emoji: EmojiItem = {
    unified: item.unified,
    symbol: unifiedToEmoji(item.unified),
    shortName: item.short_name,
    shortNames: item.short_names,
    category: categoryMap[item.category],
    sortOrder: item.sort_order,
    addedIn: parseFloat(item.added_in),
    hasImgTwitter: item.has_img_twitter,
    skinVariations: item.skin_variations
      ? <Record<SkinColour, EmojiSkinVariation>>Object.fromEntries(
          Object.entries(item.skin_variations).map(([key, value]) => [
            key,
            {
              unified: value.unified,
              symbol: unifiedToEmoji(value.unified),
              addedIn: parseFloat(value.added_in),
              hasImgTwitter: value.has_img_twitter,
            },
          ]),
        )
      : null,
  };

  if (emoji.skinVariations) {
    Object.values(emoji.skinVariations).forEach(variation => (variation.base = emoji));
  }

  return [...acc, emoji];
}, <EmojiItem[]>[]);

export const ALl_EMOJIS: Array<EmojiItem | EmojiSkinVariation> = EMOJIS.flatMap(emoji => [
  emoji,
  ...Object.values(emoji.skinVariations || {}),
]);

export const SYMBOL_EMOJI_MAP = <Record<string, EmojiItem>>(
  Object.fromEntries(ALl_EMOJIS.map(emoji => [emoji.symbol, emoji]))
);

export const EMOJI_SHORTCODE_MAP = <Record<string, EmojiItem>>(
  Object.fromEntries(EMOJIS.map(item => [item.shortName, item]))
);

const SKIP_CATEGORIES = Object.fromEntries([EmojiCategory.SkinTones].map(name => [name, true]));
const CATEGORY_ORDER = Object.fromEntries(
  [
    EmojiCategory.SmileysEmotion,
    EmojiCategory.PeopleBody,
    EmojiCategory.AnimalsNature,
    EmojiCategory.FoodDrink,
    EmojiCategory.TravelPlaces,
    EmojiCategory.Activities,
    EmojiCategory.Objects,
    EmojiCategory.Symbols,
    EmojiCategory.Flags,
  ].map((name, index) => [name, index]),
);
export const FORCE_FALLBACK = ['👩‍❤️‍💋‍👨', '👨‍❤️‍💋‍👨', '👩‍❤️‍💋‍👩', '👩‍❤️‍👨', '👨‍❤️‍👨', '👩‍❤️‍👩'];

export const emojiItemToSymbolItem = (item: EmojiItem): SymbolItem<EmojiItem> => ({
  title: `:${item.shortName}:`,
  searchTerm: item.shortName,
  value: item,
});
export const EMOJI_GROUPED: Array<SymbolGroup<EmojiItem>> = Object.entries(
  EMOJIS.reduce((acc, item) => {
    if (SKIP_CATEGORIES[item.category]) return acc;
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category] = [
      ...acc[item.category],
      { title: `:${item.shortName}:`, searchTerm: item.shortName, value: item },
    ];

    return acc;
  }, <Record<string, Array<SymbolItem<EmojiItem>>>>{}),
)
  .map(([label, data]) => ({
    label,
    data: [...data].sort((a, b) => (a.value.sortOrder > b.value.sortOrder ? 1 : -1)),
  }))
  .sort((a, b) => (CATEGORY_ORDER[a.label] > CATEGORY_ORDER[b.label] ? 1 : -1));

export const ICONS_GROUPED: Array<SymbolGroup<string>> = [
  {
    label: 'app.emoji.icons',
    data: Object.keys(materialSymbols).map(name => ({
      title: name,
      searchTerm: name,
      value: name,
    })),
  },
];
